import { useState, useCallback, useEffect } from 'react';

interface UseBlobPreviewResult {
  blobUrl: string | null;
  setBlobUrl: (url: string | null) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  previewOpen: boolean;
  setPreviewOpen: (open: boolean) => void;
  closePreview: () => void;
  cleanup: () => void;
  createBlobUrl: (blob: Blob) => string;
  createBlobUrlFromBase64: (base64Content: string, mimeType?: string) => string;
}

const useBlobPreview = (initialLoading = false): UseBlobPreviewResult => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(initialLoading);
  const [previewOpen, setPreviewOpen] = useState(false);

  const cleanup = useCallback(() => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
  }, [blobUrl]);

  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
  }, []);

  const createBlobUrl = useCallback((blob: Blob): string => {
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return url;
  }, []);

  const createBlobUrlFromBase64 = useCallback(
    (base64Content: string, mimeType = 'application/pdf'): string => {
      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      return createBlobUrl(blob);
    },
    [createBlobUrl],
  );

  return {
    blobUrl,
    setBlobUrl,
    loading,
    setLoading,
    previewOpen,
    setPreviewOpen,
    closePreview,
    cleanup,
    createBlobUrl,
    createBlobUrlFromBase64,
  };
};

export default useBlobPreview;
